import * as z from "zod";

import { ActiveFilter, ActiveFiltersMap, SavedFilterView } from "components/Filters/types";
import {
  SavedFilter,
  SearchQueryOrderDirection,
  SearchSuggestionsFieldType,
} from "types/generated";

import { SortOptions } from "./types";
import { migrateHierarchyFilterFormat } from "../helpers";

type FiltersFromSavedData = Pick<
  SavedFilterView,
  "sortOption" | "sortDirection" | "filters" | "order" | "search"
>;

const checkFilters = (defaultEmptyFilterView: FiltersFromSavedData) =>
  z
    .object({
      sortOption: z.string().nullable(),
      sortDirection: z.nativeEnum(SearchQueryOrderDirection).nullable(),
      filters: z.map(
        z.string(),
        z.object({
          values: z.array(z.string()),
          key: z.string(),
          filterName: z.string(),
          type: z.nativeEnum(SearchSuggestionsFieldType),
        })
      ),
      order: z.array(
        z.object({
          name: z.string(),
          visible: z.boolean(),
        })
      ),
      search: z.string().nullable(),
    })
    .catch(defaultEmptyFilterView);

export const getFiltersFromSavedData = (
  filter: SavedFilter["data"],
  defaultEmptyFilterView: FiltersFromSavedData
): FiltersFromSavedData => {
  try {
    const parsedFilter = JSON.parse(filter);
    const parsedFilterValue = JSON.parse(parsedFilter.value);

    const result = {
      sortOption: parsedFilterValue.sort.option ?? null,
      sortDirection: parsedFilterValue.sort.direction ?? null,
      filters: new Map(parsedFilterValue.filters) as ActiveFiltersMap,
      search: (parsedFilterValue.text && decodeURI(parsedFilterValue.text)) ?? null,
      order: parsedFilterValue.order ?? [],
    };

    const checkedFilters = checkFilters(defaultEmptyFilterView).parse(result);
    const filteredFilters: [string, ActiveFilter][] = Array.from(checkedFilters.filters).map(
      ([key, filter]: [string, ActiveFilter]) => {
        if (filter.type === SearchSuggestionsFieldType.Hierarchy) {
          return [
            key,
            {
              ...filter,
              values: migrateHierarchyFilterFormat(filter.values),
            },
          ];
        }

        return [key, filter];
      }
    );
    return { ...checkedFilters, filters: new Map(filteredFilters) as ActiveFiltersMap };
  } catch {
    return defaultEmptyFilterView;
  }
};

export const getSavedViewData = (activeFilters: ActiveFiltersMap, sortOptions: SortOptions) => {
  try {
    return {
      data: JSON.stringify({
        key: "activeFilters",
        value: JSON.stringify({
          filters: [...activeFilters],
          ...sortOptions,
        }),
      }),
      error: null,
    };
  } catch {
    return { error: new Error("View couldn't be saved"), data: null };
  }
};
